/**
 * reco-tracker.js
 *
 * Activity data layer for the cart recommendation feature ("Preporuceno za tebe").
 * Runs on every page. Tracks product-page views and cart removals into a single
 * localStorage key, and exposes a small API (window.MtgReco.store) that later
 * tasks (reco-engine.js, reco-cart section) build on.
 *
 * Storage key: mtgkarte_reco_v1
 * Shape: {
 *   v: 1,
 *   viewed:  { "<handle>": { c: <count>, t: <epoch_ms last touched>,
 *                            s: <interest score 0..1 at time t>, d: <distinct days viewed>,
 *                            dl: <last viewed day number>, ms: <total active ms on the page> }, ... },
 *   removed: [ { h: "<handle>", t: <epoch_ms> }, ... ]   // newest last
 * }
 *
 * Interest ("koliko je stvarno zainteresovan"): every product-page visit is scored 0..1
 * from active time on the page (tab visible + recent input), scroll depth, interactions
 * with the gallery / variants / quantity, copying text and adding the card to the cart
 * (= 1). Visits combine as independent evidence, s = 1 - (1 - s_old) * (1 - e), and the
 * score halves every 14 days without a new visit. interest(entry) is what the
 * recommendation code ranks by; below INTEREST_MIN the view counts as a glance.
 *
 * Logged-in customers (window.MtgRecoCustomer, rendered by theme.liquid) also keep the
 * profile on the mtgreco Worker (POST /p, signed token), so it survives devices and
 * cleared storage, and so do their removals from the cart (POST /p `removed` / `back`;
 * the server keeps the full history, the browser gets the last 7 days). The server copy
 * is cached in mtgkarte_reco_remote_v1 and merged into getViewed() / getRemoved(); it is
 * dropped as soon as nobody (or someone else) is logged in.
 *
 * All storage access is wrapped in try/catch: localStorage can throw (Safari
 * private mode, disabled storage, quota errors, etc.) and this feature must
 * never break the page if that happens - it silently no-ops instead.
 *
 * The fetch wrapper installed here only OBSERVES cart traffic (/cart/add(.js),
 * /cart/change(.js), /cart/update(.js) and plain /cart.js reads); it never
 * modifies the request/response and never throws into the page - all of its
 * logic runs in try/catch, after the real response has already been handed
 * back to the caller (via response.clone()).
 *
 * Removal detection works off `lastKnownCart` (see the block comment on it):
 * every observed cart payload updates that map, and a mutation response is
 * diffed against it. The tracker issues exactly one request of its own, the
 * /cart.js at boot that seeds the map.
 *
 * Every cart MUTATION observed here dispatches a `mtgreco:cart-changed`
 * CustomEvent on `document` (adds included), which the recommendation blocks
 * re-render on and the engine drops its memo on.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mtgkarte_reco_v1';
  var STORAGE_VERSION = 1;
  var DAY_MS = 24 * 60 * 60 * 1000;
  var MAX_AGE_MS = 7 * DAY_MS; // removed-from-cart entries
  var VIEWED_MAX_AGE_MS = 30 * DAY_MS; // views fade by interest, not by a hard 7-day cut
  var MAX_VIEWED = 60;
  var MAX_REMOVED = 10;
  var MAX_BYTES = 20 * 1024; // 20 KB

  var REMOTE_KEY = 'mtgkarte_reco_remote_v1';
  var REMOTE_TTL_MS = 5 * 60 * 1000; // full sync with the server at most every 5 minutes
  var REMOTE_WAIT_MS = 1500; // ready() never waits longer than this for the server

  // ---------------------------------------------------------------------
  // Interest scoring (pure, also used by reco-page.js / reco-engine.js and the tests)
  // ---------------------------------------------------------------------

  var HALF_LIFE_MS = 14 * DAY_MS;
  var INTEREST_MIN = 0.15; // below: a glance, not used as a suggestion or a seed
  var FULL_ATTENTION_MS = 45 * 1000; // active time that alone counts as a full read of a card page
  var BOUNCE_MS = 5 * 1000;

  function clamp01(x) {
    x = Number(x);
    return isFinite(x) ? Math.min(1, Math.max(0, x)) : 0;
  }

  /**
   * One visit -> engagement 0..1.
   * v = { activeMs, scroll (0..1 max depth), interactions (gallery, variant, quantity, copy), added }
   */
  function visitEngagement(v) {
    if (!v) return 0;
    if (v.added) return 1;
    var active = Math.max(0, Number(v.activeMs) || 0);
    var inter = Math.max(0, Math.floor(Number(v.interactions) || 0));
    if (active < BOUNCE_MS && !inter) return 0.05; // opened and left
    var e = 0.55 * Math.min(1, active / FULL_ATTENTION_MS) + 0.2 * clamp01(v.scroll) + Math.min(0.25, 0.1 * inter);
    return Math.min(0.9, e); // only adding to the cart is certainty
  }

  function decay(ageMs) {
    return Math.pow(0.5, Math.max(0, ageMs) / HALF_LIFE_MS);
  }

  /** Stored score of an entry; entries written before scoring existed get a neutral guess. */
  function baseScore(entry) {
    if (!entry) return 0;
    if (typeof entry.s === 'number' && isFinite(entry.s)) return clamp01(entry.s);
    return entry.c >= 2 ? 0.35 : 0.2;
  }

  /** Current interest 0..1: stored score, faded by time since the last visit. */
  function interest(entry, nowMs) {
    if (!entry || !entry.t) return 0;
    return baseScore(entry) * decay((nowMs || now()) - entry.t);
  }

  /** Score after a visit with engagement e, on top of what the entry held before it. */
  function combine(entry, e, nowMs) {
    var before = entry ? interest(entry, nowMs) : 0;
    return 1 - (1 - before) * (1 - clamp01(e));
  }

  function interestLevel(x) {
    return x >= 0.6 ? 'strong' : x >= 0.3 ? 'interested' : x >= INTEREST_MIN ? 'browsing' : 'glance';
  }

  /** Same handle from two sources (this browser, the server): newer visit wins, counters keep the max. */
  function mergeEntry(a, b) {
    if (!a) return b;
    if (!b) return a;
    var newer = b.t > a.t ? b : a;
    var out = {};
    Object.keys(newer).forEach(function (k) { out[k] = newer[k]; });
    out.c = Math.max(a.c || 0, b.c || 0);
    out.d = Math.max(a.d || 1, b.d || 1);
    out.ms = Math.max(a.ms || 0, b.ms || 0);
    return out;
  }

  /**
   * Short line under a suggested card that the visitor has already met:
   * kind 'removed' (was in the cart) or 'viewed' (entry with c/t/s/d). '' = no label.
   * lang: 'sr' | anything else = English. Worded without the visitor's gender.
   */
  var SEEN_LABELS = {
    sr: { removed: 'Bila ti je u korpi', again: 'Gledano više puta', viewed: 'Već gledano' },
    en: { removed: 'Was in your cart', again: 'Viewed several times', viewed: 'Viewed before' }
  };
  function seenLabel(kind, entry, lang, nowMs) {
    var L = SEEN_LABELS[String(lang || '').indexOf('sr') === 0 ? 'sr' : 'en'];
    if (kind === 'removed') return L.removed;
    if (kind !== 'viewed' || !entry) return '';
    var i = interest(entry, nowMs);
    if (i < INTEREST_MIN) return '';
    return (entry.d || 1) >= 2 || i >= 0.6 ? L.again : L.viewed;
  }

  function dayNumber(ms) {
    return Math.floor(ms / DAY_MS);
  }

  // ---------------------------------------------------------------------
  // Storage core
  // ---------------------------------------------------------------------

  function now() {
    return Date.now();
  }

  function emptyState() {
    return { v: STORAGE_VERSION, viewed: {}, removed: [] };
  }

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  /**
   * Parses/validates whatever is in localStorage. Any malformed shape
   * (wrong version, wrong types, corrupted JSON, etc.) falls back to a
   * fresh empty state rather than throwing or half-trusting bad data.
   */
  function sanitizeState(raw) {
    var state = emptyState();

    if (!isPlainObject(raw)) return state;

    if (isPlainObject(raw.viewed)) {
      Object.keys(raw.viewed).forEach(function (handle) {
        var entry = raw.viewed[handle];
        if (!handle || typeof handle !== 'string') return;
        if (!isPlainObject(entry)) return;
        var c = Number(entry.c);
        var t = Number(entry.t);
        if (!isFinite(c) || c <= 0) return;
        if (!isFinite(t) || t <= 0) return;
        state.viewed[handle] = sanitizeViewedEntry(entry, c, t);
      });
    }

    if (Array.isArray(raw.removed)) {
      raw.removed.forEach(function (entry) {
        if (!isPlainObject(entry)) return;
        var h = entry.h;
        var t = Number(entry.t);
        if (!h || typeof h !== 'string') return;
        if (!isFinite(t) || t <= 0) return;
        state.removed.push({ h: h, t: Math.floor(t) });
      });
    }

    return state;
  }

  function sanitizeViewedEntry(entry, c, t) {
    var out = { c: Math.floor(c), t: Math.floor(t) };
    if (typeof entry.s === 'number' && isFinite(entry.s)) out.s = Math.round(clamp01(entry.s) * 1000) / 1000;
    var d = Number(entry.d);
    if (isFinite(d) && d >= 1) out.d = Math.floor(d);
    var dl = Number(entry.dl);
    if (isFinite(dl) && dl > 0) out.dl = Math.floor(dl);
    var ms = Number(entry.ms);
    if (isFinite(ms) && ms > 0) out.ms = Math.floor(ms);
    return out;
  }

  function readState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyState();
      var parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (parseErr) {
        return emptyState();
      }
      return sanitizeState(parsed);
    } catch (e) {
      // localStorage unavailable (Safari private mode, disabled, etc.)
      return emptyState();
    }
  }

  /**
   * Applies the pruning/cap rules (age, count caps, byte-size cap) to a
   * state object in place, then returns it.
   */
  function pruneState(state) {
    var nowMs = now();
    var cutoff = nowMs - MAX_AGE_MS;
    var viewedCutoff = nowMs - VIEWED_MAX_AGE_MS;

    // Age-based pruning first.
    Object.keys(state.viewed).forEach(function (handle) {
      if (state.viewed[handle].t < viewedCutoff) delete state.viewed[handle];
    });
    state.removed = state.removed.filter(function (entry) {
      return entry.t >= cutoff;
    });

    // Cap viewed at MAX_VIEWED: evict lowest current interest first, oldest (smallest t) as tiebreak.
    var viewedHandles = Object.keys(state.viewed);
    if (viewedHandles.length > MAX_VIEWED) {
      viewedHandles
        .sort(function (a, b) {
          var ea = state.viewed[a];
          var eb = state.viewed[b];
          var ia = interest(ea, nowMs);
          var ib = interest(eb, nowMs);
          if (ia !== ib) return ia - ib; // least interesting first
          return ea.t - eb.t; // oldest first
        })
        .slice(0, viewedHandles.length - MAX_VIEWED)
        .forEach(function (handle) {
          delete state.viewed[handle];
        });
    }

    // Cap removed at MAX_REMOVED: oldest evicted. Keep array sorted oldest->newest.
    state.removed.sort(function (a, b) {
      return a.t - b.t;
    });
    if (state.removed.length > MAX_REMOVED) {
      state.removed = state.removed.slice(state.removed.length - MAX_REMOVED);
    }

    // Byte-size cap: evict oldest entries (across both collections) until under limit.
    var guard = 0; // safety against infinite loop
    while (serializedSize(state) > MAX_BYTES && guard < 1000) {
      guard++;
      var oldestViewedHandle = null;
      var oldestViewedTime = Infinity;
      Object.keys(state.viewed).forEach(function (handle) {
        if (state.viewed[handle].t < oldestViewedTime) {
          oldestViewedTime = state.viewed[handle].t;
          oldestViewedHandle = handle;
        }
      });
      var oldestRemovedTime = state.removed.length ? state.removed[0].t : Infinity;

      if (oldestViewedHandle === null && !state.removed.length) break; // nothing left to evict

      if (oldestRemovedTime <= oldestViewedTime) {
        state.removed.shift();
      } else if (oldestViewedHandle !== null) {
        delete state.viewed[oldestViewedHandle];
      } else {
        break;
      }
    }

    return state;
  }

  function serializedSize(state) {
    try {
      return JSON.stringify(state).length;
    } catch (e) {
      return 0;
    }
  }

  function writeState(state) {
    try {
      pruneState(state);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // Quota exceeded, storage disabled, etc. - silently no-op.
    }
  }

  // ---------------------------------------------------------------------
  // Public store API
  // ---------------------------------------------------------------------

  /**
   * Viewed products: this browser's history merged with the logged-in customer's server
   * profile (other devices, older visits). Entries keep their stored fields; rank them
   * with interest(entry).
   */
  function getViewed() {
    var viewed = readState().viewed;
    var remote = readRemote();
    if (!remote) return viewed;
    var out = {};
    Object.keys(remote.items).forEach(function (h) { out[h] = remote.items[h]; });
    Object.keys(viewed).forEach(function (h) { out[h] = mergeEntry(out[h], viewed[h]); });
    return out;
  }

  /**
   * Removed-from-cart entries, oldest first: this browser's list merged with the logged-in
   * customer's recent removals from the server (other devices), newest time per handle.
   */
  function getRemoved() {
    var state = readState();
    var remote = readRemote();
    if (!remote || !remote.removed.length) return state.removed;
    var byHandle = {};
    remote.removed.concat(state.removed).forEach(function (e) {
      if (!byHandle[e.h] || byHandle[e.h] < e.t) byHandle[e.h] = e.t;
    });
    var cutoff = now() - MAX_AGE_MS;
    return Object.keys(byHandle)
      .filter(function (h) { return byHandle[h] >= cutoff; })
      .map(function (h) { return { h: h, t: byHandle[h] }; })
      .sort(function (a, b) { return a.t - b.t; })
      .slice(-MAX_REMOVED);
  }

  /** Counts a view of `handle` (c + 1, distinct days) and returns the stored entry. */
  function trackView(handle) {
    if (!handle || typeof handle !== 'string') return null;
    try {
      var nowMs = now();
      var today = dayNumber(nowMs);
      var state = readState();
      var existing = mergeEntry(getViewed()[handle] || null, state.viewed[handle] || null);
      var entry = {};
      if (existing) Object.keys(existing).forEach(function (k) { entry[k] = existing[k]; });
      entry.c = existing ? existing.c + 1 : 1;
      entry.d = existing ? (existing.dl === today ? existing.d || 1 : (existing.d || 1) + 1) : 1;
      entry.dl = today;
      // The visit itself is scored when it ends (commitVisit); until then `s` is the old
      // score faded to now, so an entry never looks fresher than the evidence behind it.
      entry.s = Math.round(interest(existing, nowMs) * 1000) / 1000;
      entry.t = nowMs;
      state.viewed[handle] = entry;
      writeState(state);
      return entry;
    } catch (e) {
      return null;
    }
  }

  /** Stores a new score for an entry that is already viewed (visit end, add to cart). */
  function setScore(handle, base, e, extraMs) {
    try {
      var nowMs = now();
      var state = readState();
      var cur = mergeEntry(getViewed()[handle] || null, state.viewed[handle] || null);
      if (!cur) return null;
      var entry = {};
      Object.keys(cur).forEach(function (k) { entry[k] = cur[k]; });
      entry.s = Math.round(combine(base, e, nowMs) * 1000) / 1000;
      entry.t = nowMs;
      if (extraMs) entry.ms = (cur.ms || 0) + extraMs;
      state.viewed[handle] = entry;
      writeState(state);
      remotePush(handle, entry);
      return entry;
    } catch (err) {
      return null;
    }
  }

  // ---------------------------------------------------------------------
  // Visit tracking: how much attention the current product page gets
  // ---------------------------------------------------------------------

  var IDLE_MS = 30 * 1000; // no input for this long -> reading stopped
  var INTERACTION_SELECTOR = [
    '[data-media-id]', 'media-gallery', 'product-modal', '.product__media', '.product-media',
    '.product__media-toggle', 'variant-selects', 'variant-radios', 'quantity-input',
    '.product-form__input', 'input[name="quantity"]', '.product-form__quantity'
  ].join(',');

  var visit = null;

  function startVisit(handle, entry) {
    var t = now();
    visit = {
      h: handle,
      base: entry ? { c: entry.c, t: t, s: entry.s } : null, // score before this visit, already faded
      activeMs: 0, committedMs: 0, committedE: -1, scroll: 0, interactions: 0, added: false,
      lastInput: t, lastInteraction: 0
    };
    function input() { visit.lastInput = now(); }
    function onScroll() {
      input();
      try {
        var doc = document.documentElement;
        var max = Math.max(1, (doc.scrollHeight || 0) - (window.innerHeight || 0));
        visit.scroll = Math.max(visit.scroll, clamp01((window.scrollY || doc.scrollTop || 0) / max));
      } catch (e) {}
    }
    function interaction() {
      var t2 = now();
      input();
      // one burst of clicks through the gallery is one interaction, not ten
      if (t2 - visit.lastInteraction < 1500) return;
      visit.lastInteraction = t2;
      visit.interactions++;
    }
    ['mousemove', 'keydown', 'touchstart', 'wheel'].forEach(function (ev) {
      window.addEventListener(ev, input, { passive: true });
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', function (ev) {
      input();
      try {
        if (ev.target && ev.target.closest && ev.target.closest(INTERACTION_SELECTOR)) interaction();
      } catch (e) {}
    }, true);
    document.addEventListener('change', function (ev) {
      try {
        if (ev.target && ev.target.closest && ev.target.closest('form[action*="/cart/add"], ' + INTERACTION_SELECTOR)) interaction();
      } catch (e) {}
    }, true);
    document.addEventListener('copy', interaction, true);

    setInterval(function () {
      if (document.visibilityState === 'visible' && now() - visit.lastInput < IDLE_MS) visit.activeMs += 1000;
    }, 1000);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') commitVisit();
    });
    window.addEventListener('pagehide', commitVisit);
  }

  /**
   * Writes the visit's score so far. Safe to call repeatedly (tab hidden and shown again,
   * then closed): the score is always base + the whole visit, never compounded per call.
   */
  function commitVisit() {
    if (!visit) return;
    try {
      var e = visitEngagement(visit);
      var extra = visit.activeMs - visit.committedMs;
      if (e === visit.committedE && !extra) return; // hidden + pagehide back to back
      visit.committedE = e;
      visit.committedMs = visit.activeMs;
      setScore(visit.h, visit.base, e, extra);
    } catch (err) {
      // no-op
    }
  }

  /** A card went into the cart: that is interest, whether or not its page is open. */
  function markAdded(handle) {
    if (visit && visit.h === handle) {
      visit.added = true;
      commitVisit();
      return;
    }
    var cur = getViewed()[handle];
    if (cur) setScore(handle, cur, 1, 0);
  }

  // ---------------------------------------------------------------------
  // Server profile for logged-in customers (worker/, POST /p)
  // ---------------------------------------------------------------------

  function customerConfig() {
    try {
      var c = window.MtgRecoCustomer;
      if (!c || !c.token || !c.url) return null;
      var cid = String(c.token).split('.')[0];
      if (!cid) return null;
      // Same consent the analytics pixel needs; no banner / API -> allowed.
      var p = window.Shopify && window.Shopify.customerPrivacy;
      if (p && typeof p.analyticsProcessingAllowed === 'function' && p.analyticsProcessingAllowed() === false) return null;
      return { cid: cid, token: String(c.token), url: String(c.url).replace(/\/+$/, '') + '/p' };
    } catch (e) {
      return null;
    }
  }

  /** Cached server profile of the customer logged in right now, else null (and the cache is dropped). */
  function readRemote() {
    try {
      var cfg = customerConfig();
      var raw = window.localStorage.getItem(REMOTE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!cfg || !parsed || parsed.cid !== cfg.cid || !isPlainObject(parsed.items)) {
        // logged out, or a different account on this browser: never show someone else's history
        window.localStorage.removeItem(REMOTE_KEY);
        return null;
      }
      var items = {};
      Object.keys(parsed.items).forEach(function (h) {
        var entry = parsed.items[h];
        if (!isPlainObject(entry)) return;
        var c = Number(entry.c), t = Number(entry.t);
        if (!isFinite(c) || c <= 0 || !isFinite(t) || t <= 0) return;
        items[h] = sanitizeViewedEntry(entry, c, t);
      });
      return { cid: parsed.cid, at: Number(parsed.at) || 0, items: items, removed: sanitizeRemoved(parsed.removed) };
    } catch (e) {
      return null;
    }
  }

  function sanitizeRemoved(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    list.forEach(function (e) {
      if (!isPlainObject(e) || !e.h || typeof e.h !== 'string') return;
      var t = Number(e.t);
      if (isFinite(t) && t > 0) out.push({ h: e.h, t: Math.floor(t) });
    });
    return out;
  }

  function writeRemote(cid, items, removed, at) {
    try {
      window.localStorage.setItem(REMOTE_KEY, JSON.stringify({
        cid: cid, at: at === undefined ? now() : at, items: items || {}, removed: sanitizeRemoved(removed)
      }));
    } catch (e) {
      // no-op
    }
  }

  function postProfile(cfg, body, beacon) {
    var text = JSON.stringify(body);
    // text/plain keeps it a simple CORS request (no preflight)
    if (beacon && navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon(cfg.url, new Blob([text], { type: 'text/plain' }))) return Promise.resolve(null);
      } catch (e) {}
    }
    var doFetch = originalFetch || window.fetch.bind(window);
    return doFetch(cfg.url, { method: 'POST', body: text, keepalive: !!beacon, headers: { 'Content-Type': 'text/plain' } })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /** One changed entry -> server, and into the local copy of the server profile. */
  function remotePush(handle, entry) {
    var cfg = customerConfig();
    if (!cfg) return;
    var items = {};
    items[handle] = entry;
    postProfile(cfg, { t: cfg.token, items: items }, true);
    var remote = readRemote();
    if (remote) {
      remote.items[handle] = mergeEntry(remote.items[handle], entry);
      writeRemote(remote.cid, remote.items, remote.removed, remote.at);
    }
  }

  /**
   * Removal (or add-back, `back`) of one card -> server, so the customer's removed-from-cart
   * history survives devices; the cached server list is updated to match.
   */
  function remotePushRemoval(handle, t, back, knownLocally) {
    var cfg = customerConfig();
    if (!cfg) return;
    var remote = readRemote();
    var known = remote && remote.removed.some(function (e) { return e.h === handle; });
    var body = { t: cfg.token };
    body[back ? 'back' : 'removed'] = [{ h: handle, t: t }];
    // an add-back goes out only for a card known as removed (here or in the cached server list)
    if (!back || knownLocally || known) postProfile(cfg, body, true);
    if (remote) {
      var list = remote.removed.filter(function (e) { return e.h !== handle; });
      if (!back) list.push({ h: handle, t: t });
      writeRemote(remote.cid, remote.items, list, remote.at);
    }
  }

  /**
   * Upload this browser's views (so history from before logging in joins the account)
   * and pull the merged profile. Resolves when done; never rejects.
   */
  function syncRemote() {
    var cfg = customerConfig();
    if (!cfg) {
      readRemote(); // drops a cache left by a customer who logged out
      return Promise.resolve();
    }
    var cached = readRemote();
    if (cached && now() - cached.at < REMOTE_TTL_MS) return Promise.resolve();
    var state = readState();
    return postProfile(cfg, { t: cfg.token, items: state.viewed, removed: state.removed }, false).then(function (res) {
      if (res && isPlainObject(res.items)) writeRemote(cfg.cid, res.items, res.removed);
    });
  }

  var readyPromise = null;

  /** Resolves once the server profile is in (or after REMOTE_WAIT_MS). Never rejects. */
  function ready() {
    if (!readyPromise) {
      readyPromise = new Promise(function (resolve) {
        var timer = setTimeout(resolve, REMOTE_WAIT_MS);
        syncRemote().then(function () { clearTimeout(timer); resolve(); }, function () { clearTimeout(timer); resolve(); });
      });
    }
    return readyPromise;
  }

  /** Removes the customer's server profile and this browser's history. */
  function forget() {
    var cfg = customerConfig();
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(REMOTE_KEY);
    } catch (e) {}
    if (!cfg) return Promise.resolve();
    return postProfile(cfg, { t: cfg.token, forget: true }, false).then(function () {});
  }

  function trackRemoval(handle) {
    if (!handle || typeof handle !== 'string') return;
    try {
      var state = readState();
      // De-dupe: drop any existing entry for this handle, then push as newest.
      state.removed = state.removed.filter(function (entry) {
        return entry.h !== handle;
      });
      var t = now();
      state.removed.push({ h: handle, t: t });
      writeState(state);
      remotePushRemoval(handle, t, false);
    } catch (e) {
      // no-op
    }
  }

  function clearRemoval(handle) {
    if (!handle || typeof handle !== 'string') return;
    try {
      var state = readState();
      var before = state.removed.length;
      state.removed = state.removed.filter(function (entry) {
        return entry.h !== handle;
      });
      var local = state.removed.length !== before;
      if (local) {
        writeState(state);
      }
      remotePushRemoval(handle, now(), true, local);
    } catch (e) {
      // no-op
    }
  }

  // ---------------------------------------------------------------------
  // View tracking (product pages only, once per page load)
  // ---------------------------------------------------------------------

  function getProductHandleFromPath(pathname) {
    // Matches /products/<handle> and locale-prefixed variants like
    // /en/products/<handle> or /en-us/products/<handle>. Ignores any
    // trailing segments (e.g. /products/<handle>/some-variant-path is not
    // a thing in Shopify, but be defensive anyway).
    if (!pathname || typeof pathname !== 'string') return null;
    var match = pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([^\/?#]+)/i);
    if (!match || !match[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function trackCurrentPageView() {
    try {
      var handle = getProductHandleFromPath(window.location.pathname);
      if (!handle) return;
      // wait for the server profile, so the visit builds on the account's full history
      ready().then(function () {
        try {
          startVisit(handle, trackView(handle));
        } catch (e) {}
      });
    } catch (e) {
      // no-op
    }
  }

  // ---------------------------------------------------------------------
  // Cart-removal tracking via a fetch wrapper
  // ---------------------------------------------------------------------

  var CART_CHANGE_RE = /\/cart\/(change|update)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  // Plain cart-state reads. Deliberately anchored on "/cart.js" so it can never
  // match "/cart/add.js", "/cart/change.js" or "/cart/update.js" (those have a
  // slash, not a dot, after "cart") - observing a read must not be mistaken for
  // observing a mutation, otherwise reading the cart would dispatch
  // mtgreco:cart-changed and the block that reads the cart to render would
  // re-render itself forever.
  var CART_STATE_RE = /\/cart\.js(\?|$)/;

  // The un-wrapped window.fetch, captured once in installFetchWrapper(). Every
  // internal request this file makes goes through it, so the tracker's own
  // traffic is never re-observed by its own wrapper.
  var originalFetch = null;

  function requestUrlString(input) {
    try {
      if (typeof input === 'string') return input;
      // fetch() also accepts a URL instance; it has no .url, only .href.
      if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
      if (input && typeof input.url === 'string') return input.url; // Request object
      if (input && typeof input.href === 'string') return input.href; // URL-like object
      return '';
    } catch (e) {
      return '';
    }
  }

  function fetchCartState() {
    // Reads current /cart.js state once at boot to seed lastKnownCart.
    // Uses originalFetch so this read is not observed by our own wrapper
    // (which would be harmless but pointless recursion of the same seed).
    var doFetch = originalFetch || window.fetch.bind(window);
    return doFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res && res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function cartItemsByHandle(cart) {
    var map = {};
    if (!cart || !Array.isArray(cart.items)) return map;
    cart.items.forEach(function (item) {
      if (item && item.handle) {
        map[item.handle] = (map[item.handle] || 0) + (Number(item.quantity) || 0);
      }
    });
    return map;
  }

  // ---------------------------------------------------------------------
  // lastKnownCart: the running "before" picture of the cart
  //
  // The first implementation fired its own /cart.js the moment a
  // /cart/change|update request was dispatched, and diffed that "before"
  // snapshot against the response. Those two requests race: Shopify can apply
  // the mutation before serving the concurrent read, in which case the snapshot
  // already shows the post-mutation cart, both sides of the diff are equal and
  // the removal is silently missed. Rapid quantity clicks (2 -> 1 -> 0) made
  // that easy to hit.
  //
  // Instead we never fetch a "before" state per mutation: every cart payload we
  // observe (the response to /cart/add, /cart/change, /cart/update, and any
  // /cart.js read the page or the engine performs) updates this map, so it is
  // always the last state actually confirmed by the server. A mutation response
  // is diffed against it and then replaces it. One /cart.js at boot seeds it.
  // ---------------------------------------------------------------------

  var lastKnownCart = {}; // handle -> total quantity across that product's lines
  var lastKnownCartSeeded = false;
  var bootCartPromise = null;

  // Bumped every time handleCartAddResponse/handleCartChangeResponse actually
  // commit a new value to lastKnownCart. A /cart.js read captures this counter
  // at dispatch time; if a mutation commits before that read's response comes
  // back, the read is stale relative to lastKnownCart and must not overwrite
  // it with older data (see handleCartStateResponse).
  var mutationSeq = 0;

  /** Replaces lastKnownCart from a full /cart.js-shaped payload. */
  function setLastKnownCart(cart) {
    lastKnownCart = cartItemsByHandle(cart);
    lastKnownCartSeeded = true;
  }

  /**
   * /cart/add(.js) answers with the added line item(s), not a whole cart, so it
   * can only be merged in: the handles it names are definitely in the cart now.
   * The exact quantities are refreshed by the next full cart payload we observe
   * (the add also dispatches mtgreco:cart-changed, which makes the block
   * re-render and therefore re-read /cart.js).
   */
  function mergeLastKnownCartFromAdd(handles, quantities) {
    if (!handles.length) return; // nothing added successfully - nothing to merge
    handles.forEach(function (handle, index) {
      var qty = Number(quantities[index]) || 0;
      var known = Number(lastKnownCart[handle]) || 0;
      lastKnownCart[handle] = Math.max(known, qty, 1);
    });
    // Deliberately does NOT set lastKnownCartSeeded: this is a partial merge
    // (line item(s) only, not a whole cart), so it must not be mistaken for the
    // authoritative seed that unblocks handleCartChangeResponse's diff path -
    // that flag is owned by the boot seed (fetchCartState) and by full-cart
    // responses (setLastKnownCart).
  }

  // Cart observations are processed one at a time. Two responses landing in the
  // same tick would otherwise both diff against the same lastKnownCart and the
  // second one's removal would be measured against a stale "before".
  var observationChain = Promise.resolve();

  function enqueueObservation(task) {
    observationChain = observationChain.then(task).catch(function () {
      // no-op: one failed observation must not stall the chain.
    });
    return observationChain;
  }

  function diffCartsAndTrack(beforeMap, afterMap) {
    try {
      // Any handle that had qty > 0 before and is now 0 (or gone) -> removed.
      Object.keys(beforeMap).forEach(function (handle) {
        var beforeQty = beforeMap[handle] || 0;
        var afterQty = afterMap[handle] || 0;
        if (beforeQty > 0 && afterQty <= 0) {
          trackRemoval(handle);
        }
      });
      // Any handle newly present (or increased from 0) -> clear removal flag.
      Object.keys(afterMap).forEach(function (handle) {
        var beforeQty = beforeMap[handle] || 0;
        var afterQty = afterMap[handle] || 0;
        if (afterQty > 0 && beforeQty <= 0) {
          clearRemoval(handle);
        }
      });
    } catch (e) {
      // no-op
    }
  }

  // Who sent a cart request, from an "X-Mtg-Source" header (the token popup marks its own adds
  // so it does not offer tokens for tokens). Plain object, Headers or a Request object.
  function requestSource(input, init) {
    try {
      var h = init && init.headers;
      if (!h && input && typeof input === 'object' && input.headers) h = input.headers;
      if (!h) return null;
      if (typeof h.get === 'function') return h.get('X-Mtg-Source') || null;
      for (var k in h) {
        if (Object.prototype.hasOwnProperty.call(h, k) && k.toLowerCase() === 'x-mtg-source') return h[k] || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function addedHandles(data) {
    var out = [];
    if (!data) return out;
    if (Array.isArray(data.items)) {
      data.items.forEach(function (item) { if (item && item.handle) out.push(item.handle); });
    } else if (data.handle) {
      out.push(data.handle);
    }
    return out;
  }

  function handleCartAddResponse(response, source) {
    // /cart/add(.js) response is either a single line item object or, when
    // adding multiple items, an object with an "items" array. Any handle
    // present in the (successful) response should have its removal flag
    // cleared, since it is back in the cart.
    try {
      var body = response.clone().json();
      enqueueObservation(function () {
        return body
          .then(function (data) {
            try {
              if (!data) return;
              var handles = [];
              var quantities = [];
              if (Array.isArray(data.items)) {
                data.items.forEach(function (item) {
                  if (item && item.handle) {
                    handles.push(item.handle);
                    quantities.push(item.quantity);
                  }
                });
              } else if (data.handle) {
                handles.push(data.handle);
                quantities.push(data.quantity);
              }
              handles.forEach(function (handle) {
                clearRemoval(handle);
                markAdded(handle);
              });
              mergeLastKnownCartFromAdd(handles, quantities);
              if (handles.length) mutationSeq++;
              // Mirrors the change/update path: an add is a cart mutation too,
              // so anything listening (the recommendation blocks, the engine's
              // memo) has to hear about it. Safe against a render loop because
              // the resulting /cart.js read matches neither CART_ADD_RE nor
              // CART_CHANGE_RE and therefore dispatches nothing.
              dispatchCartChangedEvent({ kind: 'add', handles: handles.slice(), source: source || null });
            } catch (innerErr) {
              // no-op
            }
          })
          .catch(function () {
            // no-op: not JSON, error response, etc.
          });
      });
    } catch (e) {
      // no-op
    }
  }

  function handleCartChangeResponse(response) {
    // /cart/change(.js) and /cart/update(.js) both answer with the FULL cart,
    // so the response alone is the new state; the old state is lastKnownCart.
    try {
      var body = response.clone().json();
      enqueueObservation(function () {
        return body
          .then(function (afterCart) {
            var afterMap = cartItemsByHandle(afterCart);
            if (lastKnownCartSeeded) {
              diffCartsAndTrack(lastKnownCart, afterMap);
              setLastKnownCart(afterCart);
              mutationSeq++;
              dispatchCartChangedEvent({ kind: 'change' });
              return;
            }
            // The boot /cart.js has not come back yet (a mutation within the
            // first few hundred ms of the page). Wait for it and use it as the
            // "before" - it was issued before this mutation, so it is a valid
            // snapshot, unlike a read fired concurrently with the mutation.
            return (bootCartPromise || Promise.resolve(null)).then(function (bootCart) {
              diffCartsAndTrack(bootCart ? cartItemsByHandle(bootCart) : {}, afterMap);
              setLastKnownCart(afterCart);
              mutationSeq++;
              dispatchCartChangedEvent({ kind: 'change' });
            });
          })
          .catch(function () {
            // no-op: not JSON, error response, etc.
          });
      });
    } catch (e) {
      // no-op
    }
  }

  /**
   * Any plain /cart.js read we can see - the page's own, or the recommendation
   * engine's once-per-render read - is free, authoritative cart state. Adopting
   * it keeps lastKnownCart current without the tracker issuing requests of its
   * own. Never diffs and never dispatches: a read is not a mutation.
   */
  function handleCartStateResponse(response, dispatchMutationSeq) {
    try {
      var body = response.clone().json();
      enqueueObservation(function () {
        return body
          .then(function (cart) {
            // A mutation (add/change/update) committed to lastKnownCart while
            // this read was in flight - its response reflects state at or
            // before that commit, so adopting it now would roll lastKnownCart
            // back to stale data. Skip it; the mutation's own handler already
            // set lastKnownCart to something at least as fresh.
            if (mutationSeq !== dispatchMutationSeq) return;
            if (cart && Array.isArray(cart.items)) setLastKnownCart(cart);
          })
          .catch(function () {
            // no-op
          });
      });
    } catch (e) {
      // no-op
    }
  }

  function dispatchCartChangedEvent(detail) {
    try {
      if (typeof document !== 'undefined' && document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('mtgreco:cart-changed', { detail: detail || { kind: 'change' } }));
      }
    } catch (e) {
      // no-op
    }
  }

  function installFetchWrapper() {
    try {
      if (!window.fetch || window.fetch.__mtgRecoWrapped) return;

      originalFetch = window.fetch.bind(window);

      var wrapped = function (input, init) {
        var url = requestUrlString(input);
        var isChange = CART_CHANGE_RE.test(url);
        var isAdd = CART_ADD_RE.test(url);
        var source = isAdd ? requestSource(input, init) : null;
        var isCartState = !isChange && !isAdd && CART_STATE_RE.test(url);
        // Snapshot mutationSeq at dispatch time for a plain cart-state read, so
        // handleCartStateResponse can tell whether a mutation committed while
        // this particular read was in flight (see its own comment).
        var dispatchMutationSeq = isCartState ? mutationSeq : null;

        var promise = originalFetch(input, init);

        if (isChange || isAdd || isCartState) {
          promise
            .then(function (response) {
              try {
                if (!response || !response.ok) return;
                if (isAdd) {
                  handleCartAddResponse(response, source);
                } else if (isChange) {
                  handleCartChangeResponse(response);
                } else {
                  handleCartStateResponse(response, dispatchMutationSeq);
                }
              } catch (e) {
                // no-op - never let observation break the real call.
              }
            })
            .catch(function () {
              // Real fetch failed; nothing to observe. The original promise
              // rejection is still returned to the caller untouched below.
            });
        }

        // Always return the original (untouched) promise/response to the page.
        return promise;
      };

      wrapped.__mtgRecoWrapped = true;
      window.fetch = wrapped;
    } catch (e) {
      // no-op: if wrapping fetch fails for any reason, leave window.fetch alone.
    }
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  var W = typeof window !== 'undefined' ? window : globalThis;
  W.MtgReco = W.MtgReco || {};
  W.MtgReco.store = {
    getViewed: getViewed,
    getRemoved: getRemoved,
    trackView: trackView,
    trackRemoval: trackRemoval,
    clearRemoval: clearRemoval,
    ready: ready,
    forget: forget,
    interest: interest,
    interestLevel: interestLevel,
    seenLabel: seenLabel,
    INTEREST_MIN: INTEREST_MIN
  };
  W.MtgReco.interest = {
    visitEngagement: visitEngagement,
    interest: interest,
    combine: combine,
    level: interestLevel,
    seenLabel: seenLabel,
    mergeEntry: mergeEntry,
    decay: decay,
    INTEREST_MIN: INTEREST_MIN,
    HALF_LIFE_MS: HALF_LIFE_MS
  };
  W.MtgReco.cartEvents = { requestSource: requestSource, addedHandles: addedHandles };

  // Loaded in Node by the tests: expose the pure parts only.
  if (typeof document === 'undefined') return;

  try {
    installFetchWrapper();
    // Single /cart.js at boot to seed lastKnownCart. This is the only request
    // the tracker makes on its own, and the only reco-related request on a page
    // where no recommendation block renders.
    bootCartPromise = fetchCartState().then(function (cart) {
      if (cart && !lastKnownCartSeeded) setLastKnownCart(cart);
      return cart;
    });
    ready(); // pull the logged-in customer's server profile (no-op for guests)
    trackCurrentPageView();
  } catch (e) {
    // no-op
  }
})();
